import { Component, Input, OnDestroy, OnInit, inject, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Medication } from '../../../../../types/medications';
import { MedicationsByServiceAPI } from '../../../../../api/cms/admin/medications/all/slugs/service-code/medications-by-service.service';
import { TreatmentService } from '../../../../../api/products/categories/categories.service';
import { BehaviorSubject, Observable, Subject, of, from } from 'rxjs';
import { switchMap, scan, takeUntil, map, mergeMap, tap, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { MultiSelectComponent } from '@ui-lib/components';
import { MultiSelectOption, MultiSelectGroup } from 'libs/ui-core/src/lib/ui-components/multiselect/multiselect.model';

interface MultiSelectMedicationGroupsOption extends Medication, MultiSelectOption {}

@Component({
    selector: 'medication-filter',
    imports: [CommonModule, MultiSelectComponent],
    templateUrl: './medication-filter.component.html',
    styleUrls: ['./medication-filter.component.scss']
})
export class MedicationFilterComponent implements OnInit, OnDestroy {
  private medicationsByServiceAPI = inject(MedicationsByServiceAPI);

  @Input() set services(services: TreatmentService[]) {
    this.servicesSubject.next(services);
  }

  readonly medsSelectedChanged = output<MultiSelectMedicationGroupsOption[]>();

  private servicesSubject = new BehaviorSubject<TreatmentService[]>([]);
  private destroy$ = new Subject<void>();

  cachedMedications: { [key: string]: MultiSelectGroup<MultiSelectMedicationGroupsOption> } = {};
  medications$: Observable<MultiSelectGroup<MultiSelectMedicationGroupsOption>[]> = of([]);

  ngOnInit() {
    this.medications$ = this.servicesSubject.pipe(
      debounceTime(200),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      switchMap(services => this.loadMedications(services)),
      takeUntil(this.destroy$),
    );
  }

  onSelectionEmitterEvent(event: MultiSelectMedicationGroupsOption[]) {
    this.medsSelectedChanged.emit(event);
  }

  sortGroups(
    groups: MultiSelectGroup<MultiSelectMedicationGroupsOption>[],
  ): MultiSelectGroup<MultiSelectMedicationGroupsOption>[] {
    return [...groups].sort((a, b) => a.label.localeCompare(b.label));
  }

  loadMedications(services: TreatmentService[]): Observable<MultiSelectGroup<MultiSelectMedicationGroupsOption>[]> {
    const uncachedServiceMeds = services.filter(service => !this.cachedMedications[service.code]);

    // Mark previously visible items as invisible if their service is no longer present
    Object.values(this.cachedMedications).forEach(group => {
      if (!services.some(service => service.code === group.label)) {
        group.visible = false;
        group.options.forEach(option => {
          option.selected = false; // Clean selected of stored options
        });
      } else {
        group.visible = true;
      }
    });

    // Observable for currently cached medications
    const currentMedications$ = of(
      this.sortGroups(Object.values(this.cachedMedications)).filter(group => group.visible),
    );

    if (uncachedServiceMeds.length === 0) {
      return currentMedications$;
    }

    // Observable for new medications
    const newMedications$ = from(uncachedServiceMeds).pipe(
      mergeMap(service =>
        this.medicationsByServiceAPI.getMedications(service.code).pipe(
          map(response => {
            const meds: MultiSelectMedicationGroupsOption[] = response
              .map(medItem => ({
                ...medItem,
                label: medItem.medication_name,
              }))
              .sort((a, b) => a.medication_code.localeCompare(b.medication_code));

            return [
              {
                label: service.code,
                options: meds,
                visible: true,
              },
            ] as MultiSelectGroup<MultiSelectMedicationGroupsOption>[];
          }),
          tap(medications => {
            this.cachedMedications[service.code] = medications[0];
          }),
        ),
      ),
    );

    return currentMedications$.pipe(
      switchMap(currentMedications =>
        newMedications$.pipe(scan((acc, curr) => this.sortGroups([...acc, ...curr]), currentMedications)),
      ),
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
